// ========================================
// components/admin/PinManagement.js - Admin PIN Management
// ========================================
// PURPOSE: Allow admin to generate, view, and download upgrade PINs
// ========================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';

const PinManagement = () => {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  
  // Form state for generating PINs
  const [generateForm, setGenerateForm] = useState({
    plan: 'premium',
    quantity: 10
  });

  // ========================================
  // FETCH PINS ON MOUNT AND TAB CHANGE
  // ========================================
  useEffect(() => {
    fetchPins();
  }, [activeTab]);

  const fetchPins = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      let statusParam = '';
      if (activeTab === 'active') statusParam = '?status=unused';
      if (activeTab === 'used') statusParam = '?status=used';
      if (activeTab === 'expired') statusParam = '?status=expired';
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/pins${statusParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPins(response.data.pins || []);
    } catch (err) {
      console.error('Error fetching PINs:', err);
      // Only show error for server errors, not for empty results
      if (err.response?.status === 500) {
        setError('Server error. Please try again.');
      }
      // For 404 or other status, just show empty state
      setPins([]);
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // GENERATE NEW PINS
  // ========================================
  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setSuccess('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/pins/generate`, 
        generateForm,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setSuccess(`Generated ${response.data.generatedCount} PINs for ${generateForm.plan} plan`);
      fetchPins();
      
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate PINs');
    } finally {
      setGenerating(false);
    }
  };

  // ========================================
  // DOWNLOAD PINS AS CSV
  // ========================================
  const handleDownload = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/pins/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `agyapadie-pins-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      setError('Failed to download PINs');
    }
  };

  // ========================================
  // DELETE A PIN
  // ========================================
  const handleDelete = async (pinId) => {
    if (!window.confirm('Are you sure you want to delete this PIN?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/pins/${pinId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchPins();
      setSuccess('PIN deleted successfully');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete PIN');
    }
  };

  // ========================================
  // FORMAT DATE
  // ========================================
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // ========================================
  // GET STATUS BADGE STYLE
  // ========================================
  const getStatusBadge = (pin) => {
    const now = new Date();
    const expiresAt = new Date(pin.expiresAt);
    
    if (pin.isUsed) {
      return { text: 'USED', style: styles.badgeUsed };
    }
    if (expiresAt < now) {
      return { text: 'EXPIRED', style: styles.badgeExpired };
    }
    return { text: 'ACTIVE', style: styles.badgeActive };
  };

  const getPlanBadgeStyle = (plan) => {
    switch(plan) {
      case 'premium': return styles.badgePremium;
      case 'business': return styles.badgeBusiness;
      case 'enterprise': return styles.badgeEnterprise;
      default: return {};
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>🎫 PIN Management</h2>
        <button onClick={handleDownload} style={styles.downloadButton}>
          📥 Download CSV
        </button>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}
      {success && <div style={styles.successBox}>{success}</div>}

      {/* Generate PIN Form */}
      <div style={styles.generateCard}>
        <h3 style={styles.generateTitle}>Generate New PINs</h3>
        <div style={styles.generateForm}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Plan</label>
            <select
              value={generateForm.plan}
              onChange={(e) => setGenerateForm({...generateForm, plan: e.target.value})}
              style={styles.select}
            >
              <option value="premium">Premium - GHS 399</option>
              <option value="business">Business - GHS 999</option>
              <option value="enterprise">Enterprise - GHS 3,999</option>
            </select>
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Quantity</label>
            <select
              value={generateForm.quantity}
              onChange={(e) => setGenerateForm({...generateForm, quantity: parseInt(e.target.value)})}
              style={styles.select}
            >
              <option value={1}>1 PIN</option>
              <option value={5}>5 PINs</option>
              <option value={10}>10 PINs</option>
              <option value={20}>20 PINs</option>
              <option value={50}>50 PINs</option>
              <option value={100}>100 PINs</option>
            </select>
          </div>
          
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={styles.generateButton}
          >
            {generating ? 'Generating...' : '🔑 Generate PINs'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('active')}
          style={{...styles.tab, ...(activeTab === 'active' ? styles.activeTab : {})}}
        >
          Active PINs
        </button>
        <button
          onClick={() => setActiveTab('used')}
          style={{...styles.tab, ...(activeTab === 'used' ? styles.activeTab : {})}}
        >
          Used PINs
        </button>
        <button
          onClick={() => setActiveTab('expired')}
          style={{...styles.tab, ...(activeTab === 'expired' ? styles.activeTab : {})}}
        >
          Expired PINs
        </button>
      </div>

      {/* PINs List */}
      {loading ? (
        <div style={styles.loading}>Loading PINs...</div>
      ) : pins.length === 0 ? (
        <div style={styles.emptyState}>
          <p>No PINs found.</p>
          <p style={styles.emptySubtext}>Generate your first PIN above.</p>
        </div>
      ) : (
        <div style={styles.pinsGrid}>
          {pins.map((pin) => {
            const status = getStatusBadge(pin);
            return (
              <div key={pin._id} style={styles.pinCard}>
                <div style={styles.pinHeader}>
                  <span style={{...styles.planBadge, ...getPlanBadgeStyle(pin.plan)}}>
                    {pin.plan.toUpperCase()}
                  </span>
                  <span style={status.style}>{status.text}</span>
                </div>
                
                <div style={styles.pinCode}>
                  <span style={styles.pinCodeLabel}>PIN Code:</span>
                  <span style={styles.pinCodeValue}>{pin.pinCode}</span>
                </div>
                
                <div style={styles.pinDetails}>
                  <div style={styles.detailRow}>
                    <span>Value:</span>
                    <strong>GHS {pin.value}</strong>
                  </div>
                  <div style={styles.detailRow}>
                    <span>Expires:</span>
                    <span>{formatDate(pin.expiresAt)}</span>
                  </div>
                  {pin.usedBy && (
                    <div style={styles.detailRow}>
                      <span>Used by:</span>
                      <span>{pin.usedBy.email}</span>
                    </div>
                  )}
                  <div style={styles.detailRow}>
                    <span>Generated:</span>
                    <span>{formatDate(pin.generatedAt)}</span>
                  </div>
                </div>
                
                {!pin.isUsed && new Date(pin.expiresAt) > new Date() && (
                  <div style={styles.pinActions}>
                    <button
                      onClick={() => handleDelete(pin._id)}
                      style={styles.deleteButton}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#F5F0E6',
    minHeight: '100vh'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  title: {
    fontSize: '24px',
    color: '#1A1A1A',
    margin: 0
  },
  downloadButton: {
    padding: '10px 20px',
    backgroundColor: '#2E7D32',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
  },
  generateCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '30px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  generateTitle: {
    fontSize: '18px',
    color: '#FFD700',
    marginBottom: '15px'
  },
  generateForm: {
    display: 'flex',
    gap: '15px',
    alignItems: 'flex-end',
    flexWrap: 'wrap'
  },
  formGroup: {
    flex: '1',
    minWidth: '150px'
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginBottom: '5px'
  },
  select: {
    width: '100%',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '5px',
    fontSize: '14px'
  },
  generateButton: {
    padding: '10px 20px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    borderBottom: '1px solid #ddd',
    paddingBottom: '10px'
  },
  tab: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    borderRadius: '5px'
  },
  activeTab: {
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    fontWeight: 'bold'
  },
  pinsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '20px'
  },
  pinCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  pinHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px'
  },
  planBadge: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: 'bold',
    color: 'white'
  },
  badgePremium: { backgroundColor: '#FFD700', color: '#1A1A1A' },
  badgeBusiness: { backgroundColor: '#2E7D32', color: 'white' },
  badgeEnterprise: { backgroundColor: '#1A1A1A', color: '#FFD700' },
  badgeActive: { backgroundColor: '#d1fae5', color: '#065f46' },
  badgeUsed: { backgroundColor: '#fee2e2', color: '#dc2626' },
  badgeExpired: { backgroundColor: '#fef3c7', color: '#92400e' },
  pinCode: {
    backgroundColor: '#F5F0E6',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '15px',
    textAlign: 'center'
  },
  pinCodeLabel: {
    fontSize: '11px',
    color: '#666',
    display: 'block'
  },
  pinCodeValue: {
    fontSize: '16px',
    fontWeight: 'bold',
    fontFamily: 'monospace',
    letterSpacing: '1px'
  },
  pinDetails: {
    marginBottom: '15px'
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    marginBottom: '5px'
  },
  pinActions: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  deleteButton: {
    padding: '5px 10px',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px'
  },
  errorBox: {
    backgroundColor: '#fee2e2',
    border: '1px solid #ef4444',
    color: '#dc2626',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '20px'
  },
  successBox: {
    backgroundColor: '#d1fae5',
    border: '1px solid #10b981',
    color: '#065f46',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '20px'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#666'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    backgroundColor: 'white',
    borderRadius: '10px',
    color: '#666'
  },
  emptySubtext: {
    fontSize: '12px',
    color: '#999',
    marginTop: '5px'
  }
};

export default PinManagement;